import React, { forwardRef, useLayoutEffect, useRef } from 'react';
import { NmLoader } from '../NmLoader';
import './action-controls.css';

export type ActionSize = 'sm' | 'md' | 'lg';
export interface ActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'neutral' | 'primary' | 'ghost' | 'danger';
  size?: ActionSize;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  wrap?: boolean;
  /** Keep this boolean present in both states to reserve the loading slot. */
  loading?: boolean;
}

/** Shared action geometry; size is independent of intent and appearance. */
export const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(function ActionButton({
  variant = 'neutral', size = 'md', icon, iconPosition = 'left', wrap = false, loading, className = '', type = 'button', children, disabled, ...props
}, ref) {
  const idleContent = useRef({ children, icon, iconPosition });
  useLayoutEffect(() => {
    if (!loading) idleContent.current = { children, icon, iconPosition };
  }, [loading, children, icon, iconPosition]);

  // Reserve the indicator's geometry before the request starts. Keep the last
  // idle label while busy, even when a caller supplies a longer loading label.
  const content = loading ? idleContent.current : { children, icon, iconPosition };
  const asyncAction = loading !== undefined;
  const indicator = asyncAction
    ? <span className="ui-action__status-slot" aria-hidden="true">{loading ? <NmLoader size="xs" inline decorative /> : content.icon}</span>
    : content.icon && <span className="ui-action__icon" aria-hidden="true">{content.icon}</span>;
  return <button {...props} disabled={disabled || loading} aria-busy={loading || props['aria-busy']} ref={ref} type={type} className={`ui-action ui-action--${variant} ui-action--${size}${wrap ? ' ui-action--wrap' : ''} ${className}`.trim()}>
    {content.iconPosition === 'left' && indicator}
    {asyncAction && content.children != null ? <span className="ui-action__label">{content.children}</span> : content.children}
    {content.iconPosition === 'right' && indicator}
  </button>;
});

type ActionIconButtonProps = Omit<ActionButtonProps, 'children' | 'icon' | 'aria-label'> & {
  icon: React.ReactNode;
  label: string;
};

/** An icon action always has an accessible name and a square hit area. */
export const ActionIconButton = forwardRef<HTMLButtonElement, ActionIconButtonProps>(function ActionIconButton({
  label, icon, wrap = false, className = '', title, ...props
}, ref) {
  return <ActionButton {...props} ref={ref} title={title ?? label} aria-label={label} icon={icon} wrap={wrap} className={`ui-action--icon ${className}`} />;
});

export function ActionToggleGroup<T extends string | number>({ label, value, options, onChange, size = 'md', wrap = false, className = '' }: {
  label: string; value: T; options: ReadonlyArray<{ value: T; label: string; disabled?: boolean }>;
  onChange: (value: T) => void; size?: ActionSize; wrap?: boolean; className?: string;
}) {
  return <div role="group" aria-label={label} className={`ui-action-group ui-action-group--${size}${wrap ? ' ui-action-group--wrap' : ''} ${className}`.trim()}>
    {options.map(option => <ActionButton key={option.value} size={size} wrap={wrap} aria-pressed={option.value === value}
      disabled={option.disabled} onClick={() => onChange(option.value)}>{option.label}</ActionButton>)}
  </div>;
}
